import React from "react";
import _ from "lodash";
import slug from "slug";

import { presentations as presentationsApi } from "apis-v2/callables";
import { getLogoPath } from "common/assetUtils";
import { PresentationPrivacyType, THUMBNAIL_SIZES, TaskState, UndoType } from "common/constants";
import { ActionPermissionsObject, WorkspaceResourceType } from "common/interfaces";
import { withMaxConcurrency } from "common/utils/withMaxConcurrency";
import { getCanvasBundle } from "js/canvas";
import { appVersion, isOfflinePlayer, serverUrl } from "js/config";
import { getActionPermissionsForWorkspace, getSlidesInPresentationsCount, getUserPermissionsToResource, getWorkspaceIdForResource } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import Profiler from "js/core/profiler";
import { requestExportCache } from "js/core/services/exportCache";
import NotificationsService from "js/core/services/notifications";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { downloadFromUrl, filenameForExport, trackActivity } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog, ShowMessageDialog, ShowSnackBar } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import AppController from "js/core/AppController";
import PresentationsAdapter from "js/core/storage/presentationsAdapter";
import StorageModel from "js/core/storage/storageModel";
import * as presentationUtils from "js/core/models/utils/presentationUtils";
import ux from "js/core/services/userExperiences";

import Logos from "./logos";
import { PresentationLinks } from "./presentationLinks";
import { Slide, Slides } from "./slide";
import Thumbnails from "./thumbnails";
import { isSafari } from "../utilities/browser";

const logger = getLogger(LogGroup.PRESENTATION);

const UNSUPPORTED_TEMPLATES_FOR_EXPORT = [];

const profiler = Profiler.create({
    name: "Pres",
    type: Profiler.AVG_LOAD_TIME,
    warnTime: 400
});

function sortSlides(slides, slideRefs, getId) {
    if (!getId) {
        getId = slide => {
            return slide;
        };
    }
    slides.sort(function(a, b) {
        const result = slideRefs[getId(a)] - slideRefs[getId(b)];
        if (result !== 0) {
            return result;
        }
        //sort by id so this is consistent for all clients.
        if (a.id < b.id) {
            return -1;
        }
        if (a.id > b.id) {
            return 1;
        }
        return 0;
    });
}

class PresentationError extends Error {
    name = "PresentationError";

    constructor(messageOrError) {
        if (messageOrError instanceof Error) {
            super(messageOrError.message);
            this.stack = messageOrError.stack ?? this.stack;
            this.originalError = messageOrError;
            return;
        }

        super(messageOrError);
    }
}

export class PresentationPermissionDeniedError extends PresentationError {
    name = "PresentationPermissionDeniedError";
    statusCode = 403;
}

export class PresentationNotFoundError extends PresentationError {
    name = "PresentationNotFoundError";
    statusCode = 404;
}

const Presentation = StorageModel.extend({
    root: "presentations",
    profiler: profiler,
    readonly: isOfflinePlayer,
    // Has to be set to true in order to make sure
    // the initial model is always normalized by
    // the adapter before firing the load event
    ensurePersisted: true,

    defaults: {
        hideControls: false,
        autoPlay: false,
        autoPlayDuration: 5,
        autoLoop: false,
        showTitle: true,
        showAuthor: true,
        requireEmail: false,
        showGetDeckUI: false,
        allowPdfDownload: false,
    },

    createAdapter: function(options) {
        return new PresentationsAdapter({ ...options, model: this });
    },

    initialize: function(data, options = {}) {
        this.type = "Presentation";
        this.permissions = {
            owner: false,
            read: false,
            write: false,
            public: false,
            collaborator: false
        };
        this.disconnected = options.disconnected || false;

        this.loadPermission = options.permission || "read";

        this.workspaceId = options.workspaceId || null;

        this.slides = new Slides();
        this.slides.presentation = this;
    },

    getSlideCount: function() {
        if (this.has("slideRefs")) {
            return Object.keys(this.get("slideRefs")).length;
        } else if (this.has("slides")) {
            return this.get("slides").length;
        }
        return null;
    },

    getPrivacySetting: function() {
        if (this.get("secured")) {
            return PresentationPrivacyType.SECURED;
        } else if (this.get("public")) {
            return PresentationPrivacyType.PUBLIC;
        } else {
            return PresentationPrivacyType.PRIVATE;
        }
    },

    setPrivacySetting: async function(value) {
        switch (value) {
            case PresentationPrivacyType.PUBLIC:
                this.update({ public: true, secured: false });
                break;
            case PresentationPrivacyType.SECURED:
                this.update({ public: true, secured: true });
                break;
            case PresentationPrivacyType.PRIVATE:
                this.update({ public: false, secured: false });
                break;
        }

        await this.updatePromise;

        NotificationsService.notifyOnPresentationPrivacyChanged(this.id, value)
            .catch(err => logger.error(err, "[presentation] update() NotificationsService.notifyOnPresentationPrivacyChanged() failed", { id: this.id }));
    },

    loadPresentationLinks: function() {
        if (this.disconnected) return;
        this.links = new PresentationLinks(this);
        return this.links.load();
    },

    update: function(attrs, options = {}) {
        if (!this.permissions.write) return;

        // add `modifiedAt` on every update (if the update actually changes anything)
        const changeSet = StorageModel.prototype.update.call(this, attrs, Object.assign({ computeChangeSet: true }, options));
        if (changeSet.hasUpdates && !options.skipModifiedAt) {
            attrs = Object.assign({ modifiedAt: new Date().getTime() }, attrs);
        }

        if (!this.disconnected) {
            const updatedKeys = Object.keys(changeSet.update);
            if (updatedKeys.includes("name")) {
                NotificationsService.notifyOnPresentationRenamed(this.id)
                    .catch(err => logger.error(err, "[presentation] update() NotificationsService.notifyOnPresentationRenamed() failed", { id: this.id }));
            }
            if (updatedKeys.some(key => key.startsWith("theme_"))) {
                NotificationsService.notifyOnPresentationThemeChanged(this.id)
                    .catch(err => logger.error(err, "[presentation] update() NotificationsService.notifyOnPresentationThemeChanged() failed", { id: this.id }));
            }
        }

        return StorageModel.prototype.update.call(this, attrs, options);
    },

    reload: async function() {
        await StorageModel.prototype.reload.call(this);
        this.onSlideRefChange(this, this.get("slideRefs"), { remoteChange: true });
    },

    sortSlides: function(slides, getId) {
        if (this.has("slideRefs")) {
            slides = slides.slice(0);
            sortSlides(slides, this.get("slideRefs"), getId);
            return slides;
        } else {
            const sips = this.getSips();
            return _.sortBy(slides, slide => {
                return sips.indexOf(getId(slide));
            });
        }
    },

    getSips: function(skippedSlideIds = []) {
        let sips;
        const slideRefs = this.get("slideRefs");
        if (slideRefs) {
            sips = Object.keys(slideRefs);
            sortSlides(sips, slideRefs, i => i);
        } else {
            sips = this.get("slides") || [];
        }
        return sips.filter(slideId => !skippedSlideIds.includes(slideId));
    },

    setSips: function(sips, deletedSlides, options) {
        const slideRefs = {};
        const currentSips = this.getSips();
        deletedSlides = deletedSlides || {};

        const addedSlides = sips.filter(id => !currentSips.includes(id));
        ux.addSlideToPresentation.update({ slideIds: addedSlides });

        sips.forEach((id, index) => {
            slideRefs[id] = index;
        });

        //remove existing slides.
        currentSips.forEach(id => {
            if (!slideRefs.hasOwnProperty(id)) {
                slideRefs[id] = null;
            }
        });

        this.update({ slideRefs, deletedSlides }, options);
        // Upgrade to the latest format.
        if (this.has("slides")) {
            this.update({ slides: null });
        }

        return this.onSlideRefChange(this, this.get("slideRefs"), options);
    },

    onSlideRefChange: function(presentation, slideRefs = {}, options = {}) {
        const slidePromises = [];
        let previousRefs = presentation.previous("slideRefs") || {};

        Object.keys(slideRefs).forEach(slideId => {
            if (this.slides.has(slideId)) {
                slidePromises.push(this.slides.get(slideId));
            } else {
                slidePromises.push(this.loadSlide(slideId));
            }
        });

        Object.keys(previousRefs).forEach(slideId => {
            if (!(slideId in slideRefs)) {
                const slide = this.slides.get(slideId);
                if (slide) {
                    slide.disconnect();
                }
            }
        });

        return Promise.all(slidePromises).then(slides => {
            sortSlides(slides, slideRefs, slide => {
                return slide.id;
            });
            this.slides.set(slides);

            if (!options.silent) {
                this.trigger("slidesUpdated", slides, options);
            }
        });
    },

    setSlides: function(slides) {
        this._slidesPrepared = true;
        this.slides.set(slides, { silent: true });
    },

    prepareSlides: async function(loadSlideModels = true) {
        if (this._slidesPrepared) {
            return;
        }

        this._slidesPrepared = true;

        const slides = this.getSips()
            .map(id => new Slide({ id }, {
                autoSync: this.autoSync,
                autoLoad: false,
                userId: this.userId,
                presentation: this,
            }));

        this.slides.set(slides, { silent: true });

        if (loadSlideModels) {
            await Promise.all(slides.map(slide => slide.load()));
        }

        if (this.autoSync) {
            // listen for external changes to slides property to load and add new slide to slide collection
            this.listenTo(this, "change:slideRefs", (model, slideRefs, options) => {
                if (options.remoteChange) {
                    this.onSlideRefChange(model, slideRefs, options);
                }
            });
        }
    },

    loadSlide: function(slideId) {
        const slide = new Slide({ id: slideId }, {
            userId: this.userId,
            presentation: this,
        });
        return slide.load();
    },

    // update the presentation's SIPs list with a list of slide IDs plucked from the slides collection
    updateSIPS: function(deletedSlides, options) {
        let sips = this.slides.pluck("id");
        return this.setSips(sips, deletedSlides, options);
    },

    closePresentation: function() {
        this.stopListening();
        this.clearSlides();
    },

    clearSlides: function() {
        this._slidesPrepared = false;
        this.slides.each(model => {
            model.disconnect();
        });
        this.slides.set([], { silent: true, remoteChange: true });
    },

    addSlideFromTemplate: async function(template, index, options = {}) {
        const slideData = await getTemplateSlideData(template.id, { presentationId: this.id });

        return this.createSlides(slideData, index, options).then(slides => slides[0]);
    },

    addExistingSlides: async function(data, startIndex, options = {}) {
        if (!data) return [];

        if (!_.isArray(data)) {
            data = [data];
        }

        let index = startIndex || 0;
        let sips = this.getSips();
        let deletedSlides = Object.assign({}, this.get("deletedSlides"));
        let newSlideOptions = Object.assign({}, options, {
            userId: this.userId,
            presentation: this,
        });

        const newSlides = data.map(slideData => {
            if (slideData.id) {
                deletedSlides[slideData.id] = null;
                index = Math.max(index, sips.indexOf(slideData.id) + 1);
            }
            return new Slide({ id: slideData.id }, newSlideOptions);
        });

        data.forEach((slideData, i) => {
            !options.skipUndo && app.undoManager.set(
                UndoType.ADD_SLIDE,
                slideData.id,
                null,
                {
                    slideData: slideData,
                    index: index + i
                },
                {
                    presentation: this
                }
            );
        });

        //load the slides and then update sips!
        return Promise.all(newSlides.map(slide => {
            return slide.load();
        })).then(() => {
            // TODO: since this is async, we're not guarenteed the index is still valid. We need to have
            // one way to add an existing slide back into the ui.
            this.slides.add(newSlides, Object.assign({ silent: true, at: index }, options));
            return this.updateSIPS(deletedSlides, options);
        })
            .then(() => !this.disconnected ? Promise.all(newSlides.map(slide => NotificationsService.notifyOnSlideAdded(this.id, slide.id))) : Promise.resolve())
            .then(() => newSlides);
    },

    /**
     * This will create new slides with an undo point. Do not call this from a remote change.
     * @param {Object|Object[]} slides slide model or array of slide models (can be either instances of the Slide model or plain objects)
     * @param {Number=} startIndex insert index, defaults to 0
     * @param {Object=} options options passed to backbone models on update and add calls
     * @returns {Slide[]} array of created Slide model instances
     */
    createSlides: async function(slides, startIndex = 0, options = {}) {
        if (!slides) return [];

        if (!_.isArray(slides)) {
            slides = [slides];
        }

        const createdSlideCount = slides.length;

        const { slideTemplates: { slideTemplates } } = await app.getCanvasBundle(window.FORCE_NEW_SLIDES_VERSION ?? appVersion);
        const slideTemplateIds = slides.map(slide => slide.template_id || slide.get("template_id"));

        const slideTemplateNames = slideTemplateIds.map(id => slideTemplates[id]?.title);
        const currentSlideCount = await getSlidesInPresentationsCount(this.getWorkspaceId()).catch(() => 0);
        const eventProps = {
            slide_template_ids: slideTemplateIds,
            slide_template_names: slideTemplateNames,
            current_slide_count: currentSlideCount + createdSlideCount,
            slides_created: createdSlideCount,
        };

        trackActivity("Slide", "Create", this.id, createdSlideCount, eventProps, { audit: true });

        const newSlidesMap = {};

        const newSlides = await Promise.all(slides.map(async slide => {
            let slideModel;
            if (slide instanceof Slide) {
                // We have to make sure the slide has loaded in order
                // to get its attributes
                await slide.load();
                slideModel = _.cloneDeep(slide.attributes);
            } else {
                slideModel = _.cloneDeep(slide);
            }

            const oldId = slideModel.id;

            if (!options.skipSlidesCleanup) {
                delete slideModel.id;
                delete slideModel.thumbnails;
                delete slideModel.libraryItemId;
                delete slideModel.isGallerySlide;
                delete slideModel.assignedUser;
                delete slideModel.assignedPendingUser;
                delete slideModel.pendingUserAssignedBy;
            }

            slideModel.version = slideModel.version ?? appVersion;
            slideModel.presentationId = this.id;

            const disconnected = options.disconnected ?? this.disconnected;
            const newSlide = new Slide(slideModel, {
                ...options,
                userId: this.userId,
                presentation: this,
                disconnected
            });

            if (!disconnected) {
                await newSlide.load();
            }
            if (oldId) {
                newSlidesMap[oldId] = newSlide.id;
            }

            return newSlide;
        }));

        let countLinksTotal = 0;
        let countLinksCleared = 0;
        const remapLinks = (obj, countRemaps = 0) => {
            if (obj.hasOwnProperty("linkToSlide")) {
                ++countLinksTotal;
                if (this.slides.find(x => x.id === obj.linkToSlide)) {
                    // Maintain links to exsiting slides in the presentation by doing nothing
                } else if (newSlidesMap[obj.linkToSlide]) {
                    // Update links to duplicated slides
                    obj.linkToSlide = newSlidesMap[obj.linkToSlide];
                    ++countRemaps;
                } else {
                    // Clear orphaned links
                    delete obj.linkToSlide;
                    ++countLinksCleared;
                    ++countRemaps;
                }
            }
            for (const child of Object.values(obj)) {
                if (!!child && child instanceof Object) {
                    countRemaps = remapLinks(child, countRemaps);
                }
            }

            return countRemaps;
        };
        newSlides?.forEach(slide => {
            if (!slide?.dataState?.elements) return;
            if (remapLinks(slide.dataState.elements)) {
                slide.commit();
            }
        });

        this.slides.add(newSlides, { silent: true, at: startIndex, ...options });

        await this.updateSIPS(this.get("deletedSlides"), options);

        newSlides.forEach((slide, slideIndex) => {
            if (!options.skipUndo) {
                app.undoManager.set(
                    UndoType.ADD_SLIDE,
                    slide.id,
                    null,
                    {
                        slideData: slide.attributes,
                        index: startIndex + slideIndex
                    },
                    {
                        presentation: this
                    }
                );
            }

            if (!this.disconnected) {
                NotificationsService.notifyOnSlideAdded(this.id, slide.id)
                    .catch(err => logger.error(err, "[presentation] createSlides() NotificationsService.notifyOnSlideAdded() failed", { id: this.id, slideId: slide.id }));
            }
        });

        if (countLinksCleared) {
            ShowErrorDialog({
                title: "Not all slide links preserved",
                message: `The newly generated ${"slide".pluralize(newSlides.length > 1)} contained ${countLinksTotal} ${"link".pluralize(countLinksTotal > 1)} to other slides, but due to the destination slides not being present in this presentation, ${countLinksCleared} ${"link".pluralize(countLinksCleared > 1)} had to be cleared.`
            });
        }

        return newSlides;
    },

    destroySlides: async function(slideIds, options = {}) {
        if (!slideIds) {
            return [];
        }
        if (!_.isArray(slideIds)) {
            slideIds = [slideIds];
        }

        const sips = this.getSips();

        const justDeletedSlides = slideIds
            .map(slideId => this.slides.get(slideId))
            .filter(slide => !!slide && sips.indexOf(slide.id) !== -1);

        // We need to make sure slides are loaded before accessing their attributes
        await Promise.all(justDeletedSlides.map(slide => slide.load()));

        const presentationDeletedSlides = Object.assign({}, this.get("deletedSlides"));

        for (const slide of justDeletedSlides) {
            const slideIndex = sips.indexOf(slide.id);

            if (!options.skipUndo) {
                app.undoManager.set(
                    UndoType.DELETE_SLIDE,
                    slide.id,
                    {
                        index: slideIndex,
                        slideData: _.cloneDeep(slide.attributes)
                    },
                    null,
                    {
                        presentation: this
                    }
                );
            }

            sips.splice(slideIndex, 1);

            // Library slides won't go to deletedSlides
            if (!slide.get("libraryItemId")) {
                presentationDeletedSlides[slide.id] = new Date().getTime();
            }
        }

        if (!this.disconnected) {
            justDeletedSlides.forEach(slide => {
                NotificationsService.notifyOnSlideRemoved(this.id, slide.id)
                    .catch(err => logger.error(err, "[presentation] destroySlides() NotificationsService.notifyOnSlideRemoved() failed", { id: this.id, slideId: slide.id }));
            });
        }

        // NOTE: we reference slideIds here instead of justDeletedSlides
        // to mimic the old behavior
        const deletedSlideCount = -slideIds.length;
        const currentSlideCount = await getSlidesInPresentationsCount(this.getWorkspaceId()).catch(() => 0);
        const eventProps = {
            "slide_ids": slideIds,
            "slide_template_names": null,
            "current_slide_count": currentSlideCount + deletedSlideCount,
            "slides_created": deletedSlideCount,
        };
        trackActivity("Slide", "Delete", this.id, deletedSlideCount, eventProps, { audit: true });

        // Disconnect all deleted slides
        justDeletedSlides.forEach(slide => slide.disconnect());

        return this.setSips(sips, presentationDeletedSlides, options);
    },

    deleteSlidesPermanently: async function(slideIds, options = {}) {
        if (!slideIds) {
            return [];
        }
        if (!_.isArray(slideIds)) {
            slideIds = [slideIds];
        }

        const presentationDeletedSlides = Object.assign({}, this.get("deletedSlides"));
        if (!slideIds.some(slideId => presentationDeletedSlides[slideId])) {
            throw new Error("Slides requested for permanent deletion should already be soft deleted from presentation");
        }

        // load the slides as they have been disconnected when removed from presentation
        const slidesToDelete = await Promise.all(slideIds.map(slideId => this.loadSlide(slideId)));

        // call the destroy on the slide models to delete them in the backend
        await withMaxConcurrency(slidesToDelete.map(slide => async () => {
            await slide.destroy();
        }));
    },

    replaceSlide: function(deleteSlide, newSlides, options) {
        let insertIndex = this.getSlideIndex(deleteSlide.id);
        return this.destroySlides(deleteSlide.id, Object.assign({ silent: true }, options)).then(() => {
            return this.createSlides(newSlides, insertIndex, options).then(slides => slides[0]);
        });
    },

    batchDuplicateSlides: async function(slides, options = {}) {
        return this.createSlides(slides, options.insertIndex, options);
    },

    batchShareSlides: async function(slides, options = {}) {
        if (!slides) {
            return [];
        }
        if (!_.isArray(slides)) {
            slides = [slides];
        }

        let index = options.insertIndex || 0;
        let deletedSlides = { ...this.get("deletedSlides") };
        slides.forEach((slide, i) => {
            !options.skipUndo && app.undoManager.set(
                UndoType.ADD_SLIDE,
                slide.id,
                null,
                {
                    slideData: slide.attributes,
                    index: index + i
                },
                {
                    presentation: this
                }
            );
            if (!this.disconnected) {
                NotificationsService.notifyOnSlideAdded(this.id, slide.id)
                    .catch(err => logger.error(err, "[presentation] batchShareSlides() NotificationsService.notifyOnSlideAdded() failed", { id: this.id, slideId: slide.id }));
            }
        });
        this.slides.add(slides, Object.assign({ silent: true, at: index }, options));
        await this.updateSIPS(deletedSlides, options);
        return slides;
    },

    batchSkipSlides: function(slideModels) {
        slideModels.forEach(async slideModel => {
            this.skipSlide(slideModel, true);
        });
    },

    batchUnskipSlides: function(slideModels) {
        slideModels.forEach(async slideModel => {
            this.skipSlide(slideModel, false);
        });
    },

    duplicateSlide: function(slide, options) {
        if (!slide) return null;
        app.undoManager.openGroup();
        const promise = this.batchDuplicateSlides([slide], options);
        app.undoManager.closeGroup();
        return promise;
    },

    cutSlides: async function(slides) {
        await this.copySlides(slides);

        // if trying to delete all slides, keep first slide
        if (slides.length === this.slides.length) {
            slides = slides.slice(1);
        }

        return this.destroySlides(slides.map(slide => slide.id));
    },

    copySlides: async function(slides) {
        const sourceWorkspaceId = this.getWorkspaceId();

        // Safari requires clipboard writes to happen synchronously within a user gesture.
        // Avoid any await before writing to clipboard on Safari.
        if (isSafari) {
            // Write a minimal payload immediately to preserve the gesture.
            // Paste will resolve full slide data by id.
            const data = {
                sourceWorkspaceId,
                minimal: true,
                slides: slides.map(s => ({ id: s.id || s.get("id") }))
            };

            try {
                await clipboardWrite({
                    [ClipboardType.SLIDES]: data,
                });
            } catch (err) {
                logger.error(err, "[presentation] copySlides() clipboardWrite failed (Safari path)", { presentationId: this.id });
                ShowSnackBar({ message: "Copy failed" });
                return;
            }

            // Fire analytics after clipboard write (safe to await now).
            await this.trackSlideCopyAnalyticsAndShowFeedback(slides);
            return;
        }

        // Non-Safari: keep the existing behavior.
        // Ensure slides are done loading before copying them
        await Promise.all(slides.map(slide => slide.load()));

        const data = {
            // Add source workspace id for paste-side fallback if needed
            sourceWorkspaceId,
            // Passing attributes instead of backbone models
            slides: slides.map(s => s.attributes),
        };
        await clipboardWrite({
            [ClipboardType.SLIDES]: data,
        });

        await this.trackSlideCopyAnalyticsAndShowFeedback(slides);
    },

    trackSlideCopyAnalyticsAndShowFeedback: async function(slides) {
        const { slideTemplates: { slideTemplates } } = await app.getCanvasBundle(appVersion);
        const eventProps = {
            "source_slides": slides.map(s => s.get("id")),
            "slide_template_names": slides.map(s => slideTemplates[s.get("template_id")]?.title).filter(Boolean),
            "slide_count": slides.length,
        };
        trackActivity("Slide", "Copy", null, slides.length, eventProps, { audit: true });
        ShowSnackBar({
            message: slides.length > 1 ? `${slides.length} Slides Copied to Clipboard` : "Slide Copied to Clipboard"
        });
    },

    pasteSlides: async function(insertIndex, data, isSlideCreatedFromUserAction) {
        if (!data) {
            return null;
        }

        // Check if the source workspace is limited
        let effectiveLimitedWorkspace = false;
        if (data.sourceWorkspaceId) {
            try {
                const actionPermissions = await getActionPermissionsForWorkspace(data.sourceWorkspaceId);
                effectiveLimitedWorkspace = !actionPermissions[ActionPermissionsObject.EXTERNAL_WORKSPACES_RESOURCE_TRANSFER].use
                    ? data.sourceWorkspaceId
                    : false;
            } catch (err) {
                // If permissions can't be fetched, be conservative and restrict to source workspace
                logger.error(err, "pasteSlides() could not fetch permissions, defaulting to restricted", { workspaceId: data.sourceWorkspaceId });
                effectiveLimitedWorkspace = data.sourceWorkspaceId;
            }
        }

        if (
            !!effectiveLimitedWorkspace &&
            effectiveLimitedWorkspace !== this.getWorkspaceId()
        ) {
            ShowSnackBar({
                message: `Copied ${data.slides.length > 1 ? "Slides are" : "Slide is"} Prohibited from being pasted to another workspace`
            });
            return null;
        }

        app.undoManager.openGroup();

        let loadedSlides;
        // The second part of the condition is just to handle the case where the slides are not loaded yet.
        // but if its not Safari, It should be safe to use the full payload.
        if (data.minimal === true || (Array.isArray(data.slides) && data.slides.some(s => !s || !s.dataState))) {
            // Safari minimal payload (ids only) — load slides by id before duplicating.
            // These may come from another presentation.
            const toLoad = (data.slides || []).map(s => s.id).filter(Boolean);
            loadedSlides = await Promise.all(toLoad.map(id => this.loadSlide(id)));
        } else {
            // Full payload path (existing behavior)
            loadedSlides = data.slides;
        }

        const slideModels = await this.batchDuplicateSlides(loadedSlides, { insertIndex, isSlideCreatedFromUserAction });

        app.undoManager.closeGroup();

        const pastedSlideCount = slideModels.length;
        const { slideTemplates: { slideTemplates } } = await getCanvasBundle(appVersion);
        const eventProps = {
            "slide_ids": slideModels.map(s => s.get("id")),
            "source_slides": (data.slides || []).map(s => s.id),
            "slide_template_names": slideModels.map(s => slideTemplates[s.get("template_id")]?.title).filter(Boolean),
            "slides_created": pastedSlideCount,
        };
        trackActivity("Slide", "Paste", null, pastedSlideCount, eventProps, { audit: true });
        ShowSnackBar({
            message: slideModels.length > 1 ? `${slideModels.length} Slides Pasted from Clipboard` : "Slide Pasted from Clipboard"
        });

        return slideModels;
    },

    moveSlides: function(slideIds, index, options = {}) {
        const id = this.id;
        const slideCount = this.slides.length;
        const slideRefs = this.get("slideRefs");

        // Legacy slides-only presentations have no slideRefs yet (they are upgraded below via
        // getSips()), so only validate slideRefs against the slide count when it is present.
        if (slideRefs && Object.keys(slideRefs).length !== slideCount) {
            const err = new Error("[presentation] moveSlides() slideRefs length does not match slide count");
            logger.error(err, err.message, { slideCount, slideRefs, id });
            throw err;
        }

        const sips = this.getSips();
        if (sips.length !== slideCount) {
            const err = new Error("[presentation] moveSlides() sips length does not match slide count");
            logger.error(err, err.message, { sips, slideRefs, id });
            throw err;
        }

        // Removing moved slides
        const updatedSips = this.getSips().filter(slideId => !slideIds.includes(slideId));
        // Calcing insert index
        const insertIndex = Math.min(index, updatedSips.length);
        // Inserting moved slides
        updatedSips.splice(insertIndex, 0, ...slideIds);

        if (updatedSips.length !== slideCount) {
            const err = new Error("[presentation] moveSlides() updatedSips length does not match slide count");
            logger.error(err, err.message, { sips, slideRefs, updatedSips, id });
            throw err;
        }

        // Composing updated slide refs
        const updatedSlideRefs = {};
        updatedSips.forEach((slideId, slideIdx) => {
            updatedSlideRefs[slideId] = slideIdx;
        });

        if (Object.keys(updatedSlideRefs).length !== slideCount) {
            const err = new Error("[presentation] moveSlides() updatedSlideRefs length does not match slide count");
            logger.error(err, err.message, { sips, slideRefs, updatedSlideRefs, updatedSips, id });
            throw err;
        }

        const changeSet = this.update({ slideRefs: updatedSlideRefs });

        // Skip for legacy slides-only presentations: the changeSet only contains the diffed keys,
        // so upgrading from no slideRefs produces an empty original and a full update by design.
        if (slideRefs &&
            Object.keys(changeSet?.original?.slideRefs ?? {}).length !==
            Object.keys(changeSet?.update?.slideRefs ?? {}).length) {
            const err = new Error("[presentation] moveSlides() changeSet slideRef length does not match between original and update");
            logger.error(err, err.message, { changeSet, sips, slideRefs, updatedSlideRefs, updatedSips, id });
            throw err;
        }

        presentationUtils.troubleshootingLogs({ id, slideIds, index, sips, slideRefs, updatedSlideRefs, changeSet, logger });

        if (changeSet.hasUpdates) {
            app.undoManager.set(
                UndoType.SLIDE_ORDER,
                null,
                {
                    slideRefs,
                    movedSlides: slideIds
                },
                {
                    slideRefs: updatedSlideRefs,
                    movedSlides: slideIds
                },
                {
                    presentation: this,
                }
            );

            if (!this.disconnected) {
                slideIds.forEach(slideId => {
                    NotificationsService.notifyOnSlideMoved(this.id, slideId)
                        .catch(err => logger.error(err, "[presentation] moveSlides() NotificationsService.notifyOnSlideMoved() failed", { id: this.id, slideId }));
                });
            }
        }

        // We still have to invoke this callback even if there were no changes in order
        // to refresh the slide grid and uncollapse the slide items that were being dragged
        return this.onSlideRefChange(this, this.get("slideRefs"), options);
    },

    getSlideIndex: function(slideId) {
        //NOTE: this is slow, so avoid using this in a loop. if you need to grab many ids, call sortSlides on the ids.
        const sips = this.getSips();
        const index = sips.indexOf(slideId);
        if (index === -1) {
            return 0;
        }
        return index;
    },

    getPublicLink: function() {
        return `${location.origin}/deck/${this.id}/${slug(this.get("name"))}`;
    },

    getPlayableSlideModels: function() {
        let playableSlideModels = [];
        this.slides.forEach(model => {
            if (!model.get("isSkipped")) {
                playableSlideModels.push(model);
            }
        });
        return playableSlideModels;
    },

    getEmbedCode: function(link, inputSizeOptions = { useCustomSize: false, width: null, height: null }) {
        let embedOptions = link.get("embedOptions");

        let url = `${link.getLinkURL()}?utm_source=beautiful_player&utm_medium=embed&utm_campaign=${this.id}`;

        let divSize;
        //The customSize set by client has highest priority, then customSize set on the link in database, else default.
        if (inputSizeOptions.useCustomSize) {
            divSize = `width:${inputSizeOptions.width}px;height:${inputSizeOptions.height}px;`;
        } else if (embedOptions.useCustomSize) {
            divSize = `width:${embedOptions.width}px;height:${embedOptions.height}px;`;
        } else {
            divSize = `width:100%;height:0;padding-bottom:56.25%;`;
        }

        return `<div style="position:relative;${divSize}"><iframe allow="clipboard-write" allowfullscreen style="position:absolute; width: 100%; height: 100%;border: solid 1px #333;" src="${url}"></iframe><a href="${url}">View ${this.get("name")} on Beautiful.ai</a></div>`;
    },

    getLastModifiedData: async function() {
        return presentationsApi.getPresentationLastModifiedData({ id: this.id });
    },

    setFinishedEditingAt: async function() {
        if (this.disconnected) {
            return;
        }

        this.update({ finishedEditingAt: Date.now() });
        await this.updatePromise;
    },

    duplicate: async function(name = null) {
        return presentationsApi.copyPresentation({
            id: this.id,
            workspaceId: this.getWorkspaceId(),
            name
        });
    },

    openExternalUrl: async function(href) {
        return new Promise(resolve => {
            const isPublicLink = this.get("link")?.type === "public";

            if (!isPublicLink) {
                window.open(href, "_blank");
                resolve(true);
            } else {
                try {
                    const linkHostname = new URL(href).origin;
                    const serverHostname = new URL(serverUrl).origin;
                    if (linkHostname === serverHostname) {
                        window.open(href, "_blank");
                        resolve(true);
                        return;
                    }
                } catch {
                    // Ignore
                }

                ShowConfirmationDialog({
                    title: "Are You Sure?",
                    message: `Clicking this link will open a web page that is not controlled by Beautiful.ai. Are you sure you want to go to ${href}?`,
                    acceptCallback: () => {
                        window.open(href, "_blank");
                        resolve(true);
                    },
                    cancelCallback: () => {
                        resolve(false);
                    }
                });
            }
        });
    },

    showExportFailDialog: function() {
        this.closeExportDialog();
        const message = <div>We’re sorry, but an error occurred while exporting this presentation. Please try again or contact <a href="mailto:support@beautiful.ai?subject=Error in export">support@beautiful.ai</a> to report this issue.</div>;
        ShowMessageDialog({
            title: "Export Failure",
            message
        });
    },

    closeExportDialog: function() {
        if (this.progressDialog) {
            this.progressDialog.props.closeDialog();
            this.progressDialog = null;
        }
    },

    printToPDF: function(pdfCompressionType = "none") {
        ux.exportPresentation.register({ presentationId: this.id, subtype: ux.Subtype[ux.Type.EXPORT_PRESENTATION].PRINT_TO_PDF });
        this.enqueueExportCache("pdf", url => {
            ux.exportPresentation.complete(undefined, ({ presentationId }) => presentationId === this.id);
            window.open(url, "_blank");
        }, { title: "Preparing for print...", forPrinting: true, includeSkippedSlides: true, pdfCompressionType });
    },

    // Upon clicking export, this function checks if there are any unsupported templates being
    // used, warns the users that they cannot be exported if there are any, and then sends an export job to the backend
    enqueueExportCache: async function(assetType, downloadUrlHandler, { title, forPrinting, includeSkippedSlides, pdfCompressionType, pptxMode = "static", slideIds = null }) {
        // Ensure that any changes to the slides are saved before we begin
        await Promise.all(this.slides.map(slide => slide.finishedEditing()));

        const slidesToCheck = slideIds?.length
            ? this.slides.filter(s => slideIds.includes(s.id))
            : this.slides;
        const templatesUsed = slidesToCheck.map(s => s.attributes.template_id);
        const unsupportedSlides = _.intersection(templatesUsed, assetType === "zip" ? [] : UNSUPPORTED_TEMPLATES_FOR_EXPORT);

        const startExport = async () => {
            try {
                let cancelTask;

                this.progressDialog = ShowDialog(ProgressDialog, {
                    title,
                    message: this.slides.length > 50 ? "Your presentation is over 50 slides. This may take a bit!" : null,
                    onCancel: () => {
                        if (cancelTask) {
                            cancelTask();
                        }

                        this.progressDialog.props.closeDialog();
                        ux.exportPresentation.abandon(undefined, ({ presentationId }) => presentationId === this.id);
                    }
                });

                const onTaskChanged = async task => {
                    if (task.state === TaskState.ERROR) {
                        const error = new Error(task.errorMessage);
                        logger.error(error, "enqueueExportCache() task failed", { taskId: task.id, presentationId: this.id });
                        this.showExportFailDialog();
                        ux.exportPresentation.fail({ error, taskId: task.id }, ({ presentationId }) => presentationId === this.id);
                        return;
                    }

                    if (task.state === TaskState.PROCESSING) {
                        if (this.progressDialog) {
                            this.progressDialog.setProgress(task.stateProgressPercents);
                        }
                        return;
                    }

                    if (task.state === TaskState.FINISHED) {
                        try {
                            await downloadUrlHandler(task.signedUrl);
                        } catch (err) {
                            logger.error(err, "enqueueExportCache() downloadUrlHandler() failed", { presentationId: this.id });
                            ShowErrorDialog({ title: "Error", message: "Sorry, we could not export the presentation" });
                        }
                        this.closeExportDialog();
                    }
                };

                const taskProps = {
                    presentationId: this.get("id"),
                    presentationLinkId: this.get("link")?.id,
                    assetType,
                    pptxMode,
                    forPrinting,
                    includeSkippedSlides,
                    pdfCompressionType,
                    slideIds: slideIds?.length ? slideIds : undefined,
                };

                const { cancel } = await requestExportCache(onTaskChanged, taskProps);
                cancelTask = cancel;
            } catch (err) {
                logger.error(err, "enqueueExportCache() startExport() failed", { presentationId: this.id });
                ux.exportPresentation.fail({ error: err }, ({ presentationId }) => presentationId === this.id);
            }
        };

        if (unsupportedSlides.length) {
            ShowConfirmationDialog({
                title: "One or more slides will not be exported correctly",
                message: `Exporting the following slides is not supported: ${unsupportedSlides.join(", ")}.`,
                acceptCallback: () => startExport()
            });
        } else {
            startExport();
        }
    },

    downloadExportCache: function(assetType, { destination = "local", includeSkippedSlides = false, pdfCompressionType = "none", pptxMode = "static" }, onCallback = null) {
        const props = {
            "saved_to": destination,
        };
        const audit = !!app.user;
        if (assetType === "pdf") {
            trackActivity("Presentation", "PDFExport", null, null, props, { audit });
        } else if (assetType === "pptx") {
            trackActivity("Presentation", "PPTXExport", null, null, props, { audit });
        } else if (assetType === "zip") {
            trackActivity("Presentation", "OfflineExport", null, null, props, { audit });
        }

        const downloadUrlHandler = async url => {
            let fileName;
            fileName = filenameForExport({ name: `${this.get("name")}`, assetType });

            try {
                await downloadFromUrl(url, fileName);
                onCallback && onCallback();
                ux.exportPresentation.complete(undefined, ({ presentationId }) => presentationId === this.id);
            } catch (err) {
                logger.error(err, "downloadExportCache() downloadFromUrl() failed", { presentationId: this.id, url });

                ShowErrorDialog({ title: "Error", message: "Sorry, we could not export the presentation" });
                ux.exportPresentation.fail({ error: err }, ({ presentationId }) => presentationId === this.id);
            }
        };

        this.enqueueExportCache(assetType, downloadUrlHandler, { title: "Exporting...", forPrinting: false, includeSkippedSlides, pdfCompressionType, pptxMode });
    },

    destroy(options) {
        throw new Error("Presentation.destroy() is not supported");
    },

    generateLoadPromise(...args) {
        return StorageModel.prototype.generateLoadPromise.call(this, ...args)
            .then(() => {
                if (!this.workspaceId && !this.attributes.isDummy) {
                    return getWorkspaceIdForResource(this.id)
                        .then(workspaceId => {
                            this.workspaceId = workspaceId;
                        })
                        .catch(() => {
                            logger.warn(`[presentation] generateLoadPromise() failed to get workspaceId for presentation resource ${this.id}`);
                            this.workspaceId = AppController.workspaceId;
                        });
                } else if (!this.workspaceId) {
                    this.workspaceId = AppController.workspaceId;
                }
            })
            .then(() => {
                if (!this.get("userId")) {
                    throw new Error("This is an invalid presentation.");
                }
                if (this.disconnected) {
                    return;
                }

                // If the model was created withe loadPermission different than read then we have to
                // load permissions from the api (firebase load will only fail if user doesn't have 'read'
                // permission, but to check for 'write' or 'owner' we have to ask the API)
                // NOTE: model with this.loadPermission === 'write' is created by calling
                // presentations.getPresentation() with permission === 'write' before the presentation
                // was loaded as part of the presentations collection
                return this.getUserPermissions(this.loadPermission !== "read").then(() => {
                    if (this.permissions[this.loadPermission]) {
                        return;
                    } else {
                        throw new PresentationPermissionDeniedError("Permission denied.");
                    }
                });
            }).then(() => {
                this.applyMigrations();
                return this;
            }).catch(err => {
                this.stopListening();

                if (
                    err instanceof PresentationPermissionDeniedError ||
                    err instanceof PresentationNotFoundError
                ) {
                    throw err;
                }

                if (err.status === 403) {
                    throw new PresentationPermissionDeniedError(err);
                }
                if (err.status === 404) {
                    throw new PresentationNotFoundError(err);
                }

                throw err;
            });
    },

    applyMigrations() {
        // migrate pre-2018 themes elementStyle property
        let migratedProps = {};

        switch (this.get("theme_styleElementStyle")) {
            case "default":
            case "outline":
            case "Outlined":
            case 0:
                migratedProps["theme_styleElementStyle"] = "outlined";
                break;
            case " muted":
            case "Muted":
            case 1:
                migratedProps["theme_styleElementStyle"] = "muted";
                break;
            case "Filled":
            case 2:
                migratedProps["theme_styleElementStyle"] = "filled";
                break;
        }

        switch (this.get("theme_styleWeight")) {
            case "thin":
                migratedProps["theme_styleWeight"] = "light";
                break;
        }

        switch (this.get("theme_styleFonts")) {
            case 1:
                migratedProps["theme_styleTitleFont"] = "sourcesanspro";
                migratedProps["theme_styleBodyFont"] = "sourcesanspro";
                break;
            case 2:
                migratedProps["theme_styleTitleFont"] = "bebasneue";
                migratedProps["theme_styleBodyFont"] = "sourcesanspro";
                break;
            case 3:
                migratedProps["theme_styleTitleFont"] = "robotoslab";
                migratedProps["theme_styleBodyFont"] = "roboto";
                break;
            case 4:
                migratedProps["theme_styleTitleFont"] = "bebasneue";
                migratedProps["theme_styleBodyFont"] = "robotoslab";
                break;
            case 5:
                migratedProps["theme_styleTitleFont"] = "roboto";
                migratedProps["theme_styleBodyFont"] = "roboto";
                break;
            case 6:
                migratedProps["theme_styleTitleFont"] = "montserrat";
                migratedProps["theme_styleBodyFont"] = "montserrat";
                break;
            case 7:
                migratedProps["theme_styleTitleFont"] = "playfair";
                migratedProps["theme_styleBodyFont"] = "raleway";
                break;
        }
        migratedProps["theme_styleFonts"] = null;
        this.update(migratedProps);
    },

    disconnect: async function() {
        // Ensure all slides are correctly disconnected
        await Promise.all(this.slides.map(slide => slide.disconnect()));

        StorageModel.prototype.disconnect.call(this);
    },

    async getThumbnailUrl(slideId = null, size = "small", reloadCache = false) {
        if (!THUMBNAIL_SIZES[size]) {
            throw new Error(`Unknown thumbnail size ${size}`);
        }

        await this.loadPromise;

        if (!slideId) {
            slideId = this.getSips()[0];
        }

        // copied presentations have no slides initially
        if (!slideId) {
            await new Promise(resolve => {
                this.listenTo(this, "change:slideRefs", () => {
                    slideId = this.getSips()[0];
                    resolve();
                });
            });
        }

        return Thumbnails.getSignedUrlAndLoad(slideId, this.get("firstSlideModifiedAt"), this.id, THUMBNAIL_SIZES[size].suffix, 0, reloadCache);
    },

    getWorkspaceId() {
        return this.workspaceId;
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        const sips = this.getSips();

        //base64 encode images
        if (attrs.theme_logo) {
            const id = getLogoPath(attrs.theme_logo);
            await Logos.getSignedUrlAndLoad(id);
        }
        if (attrs.theme_logo_dark) {
            const id = getLogoPath(attrs.theme_logo_dark);
            await Logos.getSignedUrlAndLoad(id);
        }

        // Loading thumbnails of all sizes
        await Promise.all(Object.keys(THUMBNAIL_SIZES).map(size => this.getThumbnailUrl(null, size)));

        return attrs;
    },

    async fromObject(obj) {
        this.set(_.cloneDeep(obj.presentation));
    },

    getAnalytics() {
        if (arguments.length > 0) {
            throw new Error("unexpected arguments");
        }
        return {
            "presentation_id": this.get("id"),
        };
    },

    async getUserPermissions(fetchPermissions) {
        // If presentation loaded then user has read permissions
        this.permissions.read = true;

        // Is public?
        this.permissions.public = !!this.get("public");

        // Is owner?
        if (app.user?.id === this.get("userId")) {
            this.permissions.read = true;
            this.permissions.write = true;
            this.permissions.owner = true;
            return this.permissions;
        }

        if (!app.user) {
            if (this.loadPermission === "read") {
                return this.permissions;
            }

            // Anonymous can only request read permission
            throw new PresentationPermissionDeniedError("Permission denied.");
        }

        if (!fetchPermissions) {
            return this.permissions;
        }

        // Fetch all permissions (only once)
        if (!this.userPermissionsPromise) {
            this.userPermissionsPromise = (async () => {
                const permissions = await getUserPermissionsToResource({
                    workspaceId: this.getWorkspaceId(),
                    resourceId: this.id,
                    resourceType: this.get("isTemplate") ? WorkspaceResourceType.PRESENTATION_TEMPLATE : WorkspaceResourceType.PRESENTATION
                });

                return {
                    read: permissions.read.object,
                    write: permissions.write.object,
                    owner: permissions.owner,
                    public: !!this.get("public"),
                    collaborator: !permissions.owner && (permissions.read.object || permissions.write.object),
                };
            })();
        }

        this.permissions = await this.userPermissionsPromise;

        return this.permissions;
    },

    hasSmartSlides() {
        const smartSlides = this.slides
            .filter(slide => slide.dataState?.template_id !== "authoring");
        const result = !!smartSlides.length;
        return result;
    },

    skipSlide(slide, isSkipped) {
        const skippedSlides = _.cloneDeep(this.get("skippedSlides")) ?? {};
        if (isSkipped) {
            skippedSlides[slide.id] = isSkipped;
        } else {
            delete skippedSlides[slide.id];
        }
        this.update({ skippedSlides }, { replaceKeys: true });

        if (slide.loaded && !isSkipped && slide.get("isSkipped") === true) {
            // Force-remove legacy isSkipped value if slide model is loaded
            slide.update({ isSkipped: false });
        }

        this.trigger("updateSkippedSlides");
    },

    isSlideSkipped(slide, slidesMetadata = null) {
        const skippedSlides = this.get("skippedSlides") ?? {};
        if (skippedSlides[slide.id] === true) {
            return true;
        }

        if (!slide.loaded) {
            if (slidesMetadata) {
                return !!slidesMetadata[slide.id]?.isSkipped;
            }

            logger.warn(`isSlideSkipped() slide ${slide.id} is not loaded, falling back to false`, { presentationId: this.id, slideId: slide.id });
            return false;
        }

        return !!slide.get("isSkipped");
    },

    rename: async function(name) {
        name = name?.trim();
        if (!name) {
            throw new Error("Presentation name cannot be empty");
        }

        const currentName = this.get("name");
        if (name === currentName) {
            return;
        }

        const eventProps = {
            from_name: currentName,
            to_name: name,
            presentation_id: this.get("id"),
            workspace_id: this.getWorkspaceId()
        };
        trackActivity("Presentation", "Rename", null, null, eventProps, { audit: true });

        this.update({ name });
        await this.updatePromise;
    },

    hasThemeChanged() {
        return Object.keys(this.changedAttributes() ?? {}).some(key => key.startsWith("theme_") || key === "sharedThemeId");
    }
});

/**
 * Get a presentation by id.
 * @param {string} id - The id of the presentation to get.
 * @param {Object} [options] - Options for loading the presentation.
 * @param {string} [options.permission="read"] - The permission to load the presentation with.
 * @param {boolean} [options.autoSync=true] - Whether to auto-sync the presentation.
 * @returns {Promise<Presentation>} A promise that resolves to the presentation model.
 */
async function getPresentation(id, { permission = "read", autoSync = true } = {}) {
    const presentation = new Presentation({ id }, { autoSync, permission });
    await presentation.load();
    return presentation;
}

/**
 * Get a presentation model using preloaded data, this won't result in an API call as we're initializing the model with its actual data and id
 * and the presentations adapter supports preloaded initialization.
 * @param {Object} presentationData - The data of the presentation to get.
 * @param {string} workspaceId - The workspace id of the presentation.
 * @param {Object} [options] - Options for loading the presentation.
 * @param {string} [options.permission="read"] - The permission to load the presentation with.
 * @param {boolean} [options.autoSync=true] - Whether to auto-sync the presentation.
 * @returns {Promise<Presentation>} A promise that resolves to the presentation model.
 */
async function getPresentationPreloaded(presentationData, workspaceId, { permission = "read", autoSync = true } = {}) {
    const presentation = new Presentation(presentationData, { permission, autoSync, autoLoad: false });
    presentation.workspaceId = workspaceId;
    await presentation.load();
    return presentation;
}

async function getTemplateSlideData(templateId, overrides = {}) {
    const slideModel = {
        template_id: templateId
    };

    const { slideTemplates: { slideTemplates } } = await app.getCanvasBundle(window.FORCE_NEW_SLIDES_VERSION ?? appVersion);

    const Template = _.find(slideTemplates, { id: templateId });
    if (Template) {
        const template = new Template();
        Object.assign(slideModel, template.generateSlideData(), overrides);
    } else {
        Object.assign(slideModel, overrides);
    }

    return slideModel;
}

export { Presentation, PresentationPrivacyType, getPresentation, getTemplateSlideData, getPresentationPreloaded };
